import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { expenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import ExpenseStatusBadge from '../../components/expense/ExpenseStatusBadge.jsx';
import { PlusCircle, Trash2, Edit } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
];

export default function MyExpenseReportsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  async function handleDelete(report) {
    if (!confirm(`Delete draft "${report.claimRef}${report.claimPurpose ? ' — ' + report.claimPurpose : ''}"? This removes the draft, its line items, and any uploaded receipts.`)) return;
    setDeletingId(report.rowKey);
    try {
      await expenseApi.remove(report.rowKey);
      setReports((prev) => prev.filter((r) => r.rowKey !== report.rowKey));
      toast.success('Draft deleted');
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  }

  useEffect(() => {
    async function load() {
      try {
        const res = await expenseApi.list();
        setReports(res.reports || []);
      } catch (err) {
        toast.error(`Failed to load expense reports: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = reports;
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        r.claimPurpose?.toLowerCase().includes(s) ||
        r.claimRef?.toLowerCase().includes(s) ||
        r.project?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [reports, statusFilter, search]);

  const columns = [
    {
      key: 'claimRef',
      label: 'Ref',
      render: (r) => <span className="font-mono text-xs font-semibold text-profun-cyan">{r.claimRef}</span>,
    },
    {
      key: 'claimPurpose',
      label: 'Purpose',
      render: (r) => <span className="font-medium truncate max-w-[250px] block">{r.claimPurpose || '\u2014'}</span>,
    },
    {
      key: 'totalClaimedSAR',
      label: 'Total (SAR)',
      render: (r) => <span className="font-mono font-semibold">{(r.totalClaimedSAR || 0).toFixed(2)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <ExpenseStatusBadge status={r.status} />,
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (r) => {
        if (!r.createdAt) return '\u2014';
        return new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      },
    },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        if (r.status === 'draft') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
              disabled={deletingId === r.rowKey}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
              title="Delete draft"
              aria-label="Delete draft"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          );
        }
        if (r.status === 'rejected') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/expenses/new?edit=${encodeURIComponent(r.rowKey)}`); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
              title="Edit & resubmit"
              aria-label="Edit & resubmit"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Expenses</h1>
          <p className="text-muted-foreground">
            {reports.length} total &middot; {reports.filter((r) => r.status === 'pending').length} pending
          </p>
        </div>
        <Button onClick={() => navigate('/expenses/new')}>
          <PlusCircle className="h-4 w-4 mr-2" />
          New Expense Report
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={cn(
              'rounded-md border px-3.5 py-1.5 text-sm transition-colors cursor-pointer',
              statusFilter === f.value
                ? 'border-primary bg-primary/10 font-semibold text-primary'
                : 'border-input text-muted-foreground hover:bg-muted'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search expenses..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[400px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage={reports.length === 0 ? 'No expense reports yet.' : 'No reports match your filters.'}
        onRowClick={(r) => navigate(`/expenses/${encodeURIComponent(r.rowKey)}`)}
        keyFn={(r) => r.rowKey}
      />
    </div>
  );
}
